import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { IonInfiniteScroll, IonSearchbar, Platform } from '@ionic/angular';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { TableColumn } from '../../../core/models/table-column';
import { MatTableDataSource } from '@angular/material/table';
import { ExpensesColumnsEnum } from '../../../core/state-mangement/entities/expenses-items/expenses-columns.enum';
import { TextKeys } from '../../../core/i18n/text-keys';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from '../../../core/services/toast-service';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  tap,
} from 'rxjs/operators';
import { MaterialFilterOptionsModel } from '../../../core/models/material/material-filter-options.model';
import { MaterialItemsService } from '../../../core/state-mangement/entities/material-items/material-items.service';
import { MaterialItemsQuery } from '../../../core/state-mangement/entities/material-items/material-items.query';
import { OrderListItemModel } from '../../../core/models/order/order-list-item.model';
import { SettingsQuery } from '../../../core/state-mangement/settings/settings.query';
import { OrderItemsQuery } from '../../../core/state-mangement/entities/order-items/order-items.query';
import { TranslateService } from '@ngx-translate/core';
import { HelpManualService } from '../../../core/services/help-manual.service';
import { LocalStateKeys } from '../../../core/state-mangement/local-state-keys';
import { OrderItemsStoreService } from '../../../core/state-mangement/order-items-store-service';
import { StoreInfoParams } from '../../../core/models/store-info-params';
import { MaterialModel } from '../../../core/models/material/material-model';
import { isComponentVisible } from '../../../core/helpers/helper-functions';
import { AlertService } from '../../../core/services/alert.service';
import { Sort } from '@angular/material/sort';
import { sortByOptions } from '@datorama/akita';
import { SessionQuery } from 'src/app/core/state-mangement/session/session.query';
import { ChronoUserPersonalRights } from 'src/app/core/models/client-models';

@Component({
  selector: 'app-material-list',
  templateUrl: './material-list.component.html',
  styleUrls: ['./material-list.component.scss'],
})
export class MaterialListComponent implements OnInit, OnDestroy {
  orderStoreKey: LocalStateKeys;

  @ViewChild(IonInfiniteScroll) infiniteScroll: IonInfiniteScroll;
  @ViewChild(IonSearchbar, { static: true }) searchBar: IonSearchbar;

  currentOrder: OrderListItemModel;
  currentOrder$: Observable<OrderListItemModel>;
  materialTitle$: Observable<string>;
  searchFilter$ = new BehaviorSubject<string>('');
  materialList$: Observable<MaterialModel[]>;
  filterOptions$: Observable<MaterialFilterOptionsModel>;
  tableColumns$: Observable<TableColumn[]>;
  displayedColumns: string[] = [];
  dataSource: MatTableDataSource<MaterialModel>;
  selectedIndex: BehaviorSubject<number> = new BehaviorSubject<number>(-1);
  columns = ExpensesColumnsEnum;
  editable$: Observable<boolean>;
  isReadOnly$: Observable<boolean>;
  isLoading$ = this.materialItemsQuery.selectLoading();
  lastPage$ = this.materialItemsQuery.lastPage$;
  showTable$: Observable<boolean> = this.platform.resize.pipe(
    startWith(
      this.platform.width() > this.settingsQuery.getValue()?.smallScreenWidth
    ),
    map(
      () =>
        this.platform.width() > this.settingsQuery.getValue()?.smallScreenWidth
    )
  );
  texts = TextKeys;
  topic = HelpManualService.topics.material;

  private subscriptions = new Subscription();
  private orderItemsQuery: OrderItemsQuery;
  private activeAlert = false;

  constructor(
    public materialItemsService: MaterialItemsService,
    private materialItemsQuery: MaterialItemsQuery,
    private location: Location,
    private router: Router,
    private elementRef: ElementRef,
    private route: ActivatedRoute,
    private toastService: ToastService,
    private settingsQuery: SettingsQuery,
    private translateService: TranslateService,
    private orderItemsStoreService: OrderItemsStoreService,
    private platform: Platform,
    private alertService: AlertService,
    private sessionQuery: SessionQuery
  ) {}

  @HostListener('document:keydown.enter', ['$event'])
  onEnterKeyPress(event: KeyboardEvent): void {
    if (isComponentVisible(this.elementRef, this.activeAlert)) {
      const index = this.selectedIndex.getValue();
      const item = this.materialItemsQuery.getActive();
      if (index !== -1 && item?.authorisation?.edit) {
        this.openEditPage();
      }
    }
  }

  @HostListener('document:keydown.delete', ['$event'])
  onDeleteKeyPressed(event: KeyboardEvent): void {
    if (isComponentVisible(this.elementRef, this.activeAlert)) {
      const index = this.selectedIndex.getValue();
      const item = this.materialItemsQuery.getActive();
      if (index !== -1 && item?.authorisation?.edit) {
        this.askDeleteQuestion();
      }
    }
  }

  ngOnInit() {
    const value = this.router.getCurrentNavigation().extras
      .state as StoreInfoParams;
    if (!value) {
      this.location.back();
    }
    this.orderStoreKey = value.orderStoreKey;
    this.orderItemsQuery = this.orderItemsStoreService.getOrCreateStore(
      this.orderStoreKey
    ).query;

    this.materialItemsService.clearCache();
    this.materialItemsService.clearFilter();

    this.currentOrder = this.orderItemsQuery.getActive();
    this.currentOrder$ = this.orderItemsQuery.selectActive();
    this.materialTitle$ = this.getNoteOrderTitle();

    if (!this.currentOrder) {
      this.toastService.presentInfoToast({
        message: this.translateService.instant(
          TextKeys.general.selectOrderFirst
        ),
      });
      this.location.back();
    }
    this.materialList$ = this.materialItemsQuery.selectAll();
    this.subscriptions.add(
      this.materialList$.subscribe((list) => {
        this.dataSource = new MatTableDataSource<MaterialModel>(list);
      })
    );
    this.tableColumns$ = this.materialItemsQuery.columns$;

    this.registerInfiniteScrollEnd();
    this.registerFilterValueChange();

    this.filterOptions$ = this.materialItemsQuery.filterOptions$;
    this.materialItemsService.getNextPage(this.orderStoreKey);
    setTimeout(() => this.searchBar.setFocus(), 500);

    this.subscriptions.add(
      this.selectedIndex.subscribe((index) => {
        this.materialItemsService.setActive(this.dataSource.data[index]);
      })
    );

    this.editable$ = this.materialItemsQuery
      .selectActive()
      .pipe(map((item) => item?.authorisation.edit));

    this.isReadOnly$ = this.sessionQuery.isOrderReadOnly$;
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.materialItemsService.clearCache();
  }

  onHover(index: number) {
    this.selectedIndex.next(index);
  }

  loadData(event: Event) {
    this.materialItemsService.getNextPage(this.orderStoreKey);
    (event.target as unknown as IonInfiniteScroll).complete();
  }

  getTranslatedTitle(): Observable<string> {
    return this.translateService.get(TextKeys.material.titleList);
  }

  getNoteOrderTitle(): Observable<string> {
    return combineLatest({
      translatedTitle: this.getTranslatedTitle(),
      order: this.currentOrder$,
    }).pipe(
      map(({ translatedTitle, order }) => {
        const formattedString = order?.orderNumber + ' - ' + order?.orderName;
        return `${translatedTitle}: ${formattedString}`;
      })
    );
  }

  ionViewWillEnter() {
    if (this.orderStoreKey) {
      this.materialItemsService.clearCache();
      this.materialItemsService.getNextPage(this.orderStoreKey);
    }
  }

  onEdit(event) {
    event.stopImmediatePropagation();

    if (
      this.sessionQuery.getValue()?.user?.user?.userRights?.order ===
      ChronoUserPersonalRights.ReadOnly
    ) {
      return;
    }

    this.openEditPage();
  }
  onSort(event: Sort) {
    this.materialItemsService.onSort(
      event.active,
      event.direction,
      this.orderStoreKey
    );
  }

  onDoubleClick() {
    const item = this.materialItemsQuery.getActive();
    if (item?.authorisation?.edit) {
      this.openEditPage();
    }
  }

  openEditPage() {
    this.router.navigate(['edit-material'], {
      relativeTo: this.route,
      state: {
        orderStoreKey: this.orderStoreKey,
      } as StoreInfoParams,
    });
  }

  onNew(event) {
    event.stopImmediatePropagation();
    if (
      this.sessionQuery.getValue()?.user?.user?.userRights?.order ===
      ChronoUserPersonalRights.ReadOnly
    ) {
      return;
    }
    this.router.navigate(['new-material'], {
      relativeTo: this.route,
      state: {
        orderStoreKey: this.orderStoreKey,
      } as StoreInfoParams,
    });
  }

  onCancelClick() {
    this.location.back();
  }

  onCancelWithEvent(event) {
    event.stopImmediatePropagation();
    this.onCancelClick();
  }

  onClick(row: number) {
    this.selectedIndex.next(row);
  }

  onDelete(event) {
    event.stopImmediatePropagation();

    if (
      this.sessionQuery.getValue()?.user?.user?.userRights?.order ===
      ChronoUserPersonalRights.ReadOnly
    ) {
      return;
    }

    this.selectedIndex.next(-1);
    this.askDeleteQuestion();
  }

  deleteMaterial() {
    this.activeAlert = false;
    this.materialItemsService
      .deleteMaterial(this.materialItemsQuery.getActive())
      .pipe(tap(() => this.materialItemsService.clearCache()))
      .subscribe({
        next: (data) => {
          this.materialItemsService.getNextPage(this.orderStoreKey);
          this.toastService.presentInfoToast({ message: data.message });
        },
        error: (error) => {
          this.materialItemsService.getNextPage(this.orderStoreKey);
          if (error.error?.message) {
            this.toastService.presentErrorToast({
              message: error.error.message,
            });
          }
        },
      });
  }

  onDeleteCancel() {
    this.activeAlert = false;
  }

  private registerFilterValueChange() {
    this.subscriptions.add(
      this.searchBar.ionInput
        .pipe(
          debounceTime(100),
          map((event) => (event.target as HTMLInputElement).value),
          distinctUntilChanged(),
          tap((value) => {
            this.selectedIndex.next(0);
            this.materialItemsService.filterValueChange(
              value,
              this.orderStoreKey
            );
          })
        )
        .subscribe()
    );
  }

  private registerInfiniteScrollEnd() {
    this.subscriptions.add(
      this.materialItemsQuery.lastPage$
        .pipe(
          tap((value) => {
            if (value) {
              this.infiniteScroll.disabled;
            }
          })
        )
        .subscribe()
    );
  }

  private askDeleteQuestion() {
    this.activeAlert = true;
    this.alertService.showOkCancelAlert(
      this.translateService.instant(TextKeys.material.deleteQuestion),
      this.translateService.instant(TextKeys.material.title),
      this.deleteMaterial.bind(this),
      this.onDeleteCancel.bind(this)
    );
  }

  protected readonly sortByOptions = sortByOptions;
}
